import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { each, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { clearEkengUser } from '../../../redux/modules/user';
import UserFormContent from '../UserFormContent';
import usePrevious from '../../../hooks/usePrevious';
import useScrollToFormErrorElement from '../../../hooks/useScrollToFormErrorElement';
import ScrollToTopPage from '../../ScrollToTopPage';
import Navigation from './Navigation';
import PassportIdInfo from '../../FormComponents/PassportIdInfoWrapper/PassportIdInfo';
import { bindContractMemberData, setSignerInitialData } from '../../../helpers/contractHelpers';
import { selectSortedCountries, selectSortedRAStates } from '../../../redux/modules/countries';

import commonStyles from '../../../containers/Registration/userFormCommonStyles.module.scss';

const SignerStep = ({ goNext, blockNextSteps, params }) => {
  const {
    data: contractData,
    signerBackendErrors,
    handleActionsBeforeNext,
    totalPrice,
    handleOpenMobileSummary,
    hasLanguageSensitiveNames,
    hasAddressInputsLanguageSensitiveNames,
    isDeviceLgSize,
    setStepNavigationBox,
    contractSignerSchema,
  } = params;

  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const { i18n } = useTranslation();

  const user = useSelector((store) => store.userReducer.user);
  const ekengUser = useSelector((store) => store.userReducer.ekengUser);
  const countries = useSelector((state) => selectSortedCountries(state, i18n.language));
  const states = useSelector((state) => selectSortedRAStates(state, i18n.language));

  const { signer } = contractData;

  const [initialData, setInitialData] = useState({});
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [submitCount, setSubmitCount] = useState(0);
  const previousCount = usePrevious(submitCount);
  const dispatch = useDispatch();

  useEffect(() => {
    setSignerInitialData(user, ekengUser, signer, setInitialData, setIsFormVisible);
  }, [user, signer, ekengUser]);

  useEffect(() => {
    if (isEmpty(initialData) && !isEmpty(countries) && !isEmpty(states)) {
      setInitialData((prevState) => ({
        ...prevState,
        country: countries[0],
        state: states[0],
      }));
    }
  }, [initialData, countries, states]);

  useEffect(() => () => dispatch(clearEkengUser()), [dispatch]);

  const {
    register,
    setValue,
    errors,
    triggerValidation,
    clearError,
    getValues,
    handleSubmit,
    formState,
    setError,
    watch,
  } = useForm({
    validationSchema: contractSignerSchema,
    mode: 'onBlur',
  });

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  useEffect(() => {
    clearError();
  }, [clearError, i18n.language]);

  useEffect(() => {
    setSubmitCount(formState.submitCount);
  }, [formState.submitCount]);

  useScrollToFormErrorElement(errors, submitCount, previousCount);

  useEffect(() => {
    if (!isEmpty(errors)) {
      blockNextSteps();
    }
  }, [errors, blockNextSteps]);

  useEffect(() => {
    if (signerBackendErrors) {
      const errorList = [];
      each(signerBackendErrors, (e, key) => {
        errorList.push({
          type: 'backend',
          name: key,
          message: `backendErrors.${e}`,
        });
      });
      const element = document.getElementsByName(errorList[0].name)[0];
      if (element) {
        setTimeout(() => element.scrollIntoView({ block: 'center' }), 1000);
      }
      setTimeout(() => setError(errorList));
    }
  }, [setError, signerBackendErrors]);

  const next = useCallback(
    (data) => {
      const formData = bindContractMemberData(data);
      handleActionsBeforeNext(formData);
      goNext();
    },
    [goNext, handleActionsBeforeNext],
  );

  return (
    <div id={commonStyles.form}>
      {
        !signerBackendErrors && <ScrollToTopPage withDelay />
      }
      <Navigation
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        handleOpenMobileSummary={handleOpenMobileSummary}
        totalPrice={totalPrice}
        handleSubmit={handleSubmit}
        next={next}
        errorsLength={errorsLength}
      />
      {(isFormVisible || isAuthenticated) && (
      <form>
        <UserFormContent
          initialData={initialData}
          formProps={{
            register,
            getValues,
            setValue,
            errors,
            triggerValidation,
            clearError,
            watch,
          }}
          hasLanguageSensitiveNames={hasLanguageSensitiveNames}
          hasAddressInputsLanguageSensitiveNames={hasAddressInputsLanguageSensitiveNames}
          hasEmailPassword
          passportIdInfoComponent={<PassportIdInfo />}
        />
      </form>
      )}
    </div>
  );
};

SignerStep.propTypes = {
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  params: PropTypes.shape({
    type: PropTypes.string.isRequired,
    data: PropTypes.shape({
      signer: PropTypes.shape({}),
    }).isRequired,
    signerBackendErrors: PropTypes.shape({}),
    handleActionsBeforeNext: PropTypes.func,
    hasLanguageSensitiveNames: PropTypes.bool,
    hasAddressInputsLanguageSensitiveNames: PropTypes.bool,
    totalPrice: PropTypes.number,
    isDeviceLgSize: PropTypes.bool.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    setStepNavigationBox: PropTypes.func.isRequired,
    contractSignerSchema: PropTypes.shape({}),
  }),
};

SignerStep.defaultProps = {
  params: {
    signerBackendErrors: null,
    handleActionsBeforeNext: () => {},
    hasLanguageSensitiveNames: false,
    hasAddressInputsLanguageSensitiveNames: false,
    totalPrice: null,
  },
};

export default SignerStep;
